import React, { useEffect, useState, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Button,
  Spin,
  Empty,
  Alert,
  Tag,
  Modal,
  message,
  Tabs,
  Row,
  Col
} from 'antd';
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  EuroOutlined,
  ExclamationCircleOutlined,
  StarOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import ReviewForm from '../../components/reviews/ReviewForm';
import './MyAppointmentsPage.scss';

const { confirm } = Modal;

const MyAppointmentsPage = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { appointmentStore } = useStores();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [selectedAppointmentForReview, setSelectedAppointmentForReview] = useState(null);
  const [reviewedAppointments, setReviewedAppointments] = useState(new Set());

  useEffect(() => {
    appointmentStore.fetchMyAppointments();
  }, []);

  const getStatusColor = (status) => {
    const colors = {
      PENDING: 'orange',
      CONFIRMED: 'blue',
      COMPLETED: 'green',
      CANCELLED: 'red'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status) => {
    const texts = {
      PENDING: t('appointments.status.pending'),
      CONFIRMED: t('appointments.status.confirmed'),
      COMPLETED: t('appointments.status.completed'),
      CANCELLED: t('appointments.status.cancelled')
    };
    return texts[status] || status;
  };

  const handleCancelAppointment = (appointment) => {
    confirm({
      title: t('appointments.cancelModal.title'),
      icon: <ExclamationCircleOutlined />,
      content: t('appointments.cancelModal.message', {
        date: dayjs(appointment.appointment_date).format('MMMM D, YYYY'),
        time: appointment.start_time?.slice(0, 5) || 'N/A'
      }),
      okText: t('appointments.cancelModal.yesCancel'),
      okType: 'danger',
      cancelText: t('common.no'),
      async onOk() {
        const result = await appointmentStore.cancelAppointment(appointment.id);
        if (result.success) {
          message.success(t('appointments.cancelSuccess'));
        } else {
          message.error(result.error || t('appointments.cancelFailed'));
        }
      }
    });
  };

  const handleBookNew = () => {
    navigate('/services');
  };

  const openReviewModal = (appointment) => {
    setSelectedAppointmentForReview(appointment);
    setReviewModalVisible(true);
  };

  const handleReviewSuccess = () => {
    // Mark this appointment as reviewed locally
    if (selectedAppointmentForReview) {
      setReviewedAppointments(prev => new Set([...prev, selectedAppointmentForReview.id]));
    }
    // Refresh appointments to get updated review status
    appointmentStore.fetchMyAppointments();
  };

  // Memoize filtered appointments to prevent re-renders
  const upcomingAppointments = useMemo(() => {
    if (!appointmentStore.myAppointments || appointmentStore.myAppointments.length === 0) {
      return [];
    }
    return appointmentStore.getUpcoming(appointmentStore.myAppointments);
  }, [appointmentStore.myAppointments]);

  const pastAppointments = useMemo(() => {
    if (!appointmentStore.myAppointments || appointmentStore.myAppointments.length === 0) {
      return [];
    }
    return appointmentStore.getPast(appointmentStore.myAppointments);
  }, [appointmentStore.myAppointments]);

  const renderAppointmentCard = (appointment) => {
    const serviceName = i18n.language === 'el'
      ? (appointment.service_name_el || appointment.service_name_en)
      : appointment.service_name_en;

    return (
      <Card key={appointment.id} className="appointment-card" hoverable>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={16}>
            <div className="appointment-header">
              <h3>{serviceName}</h3>
              <Tag color={getStatusColor(appointment.status)}>{getStatusText(appointment.status)}</Tag>
            </div>

            <div className="appointment-details">
              <div className="detail-item">
                <EnvironmentOutlined />
                <span>{appointment.partner_name}</span>
              </div>

              <div className="detail-item">
                <CalendarOutlined />
                <span>{dayjs(appointment.appointment_date).format('MMMM D, YYYY')}</span>
              </div>

              {appointment.requires_booking !== false && (
                <div className="detail-item">
                  <ClockCircleOutlined />
                  <span>
                    {appointment.start_time?.slice(0, 5) || 'N/A'} - {appointment.end_time?.slice(0, 5) || 'N/A'}
                  </span>
                </div>
              )}

              {appointment.requires_booking === false && (
                <div className="detail-item">
                  <ClockCircleOutlined />
                  <Tag color="blue">{t('appointments.serviceRequest')}</Tag>
                </div>
              )}

              <div className="detail-item">
                <EuroOutlined />
                <span className="price">€{appointment.service?.price || 'N/A'}</span>
              </div>
            </div>

            {appointment.customer_notes && (
              <div className="notes-section">
                <strong>{t('appointments.yourNotes')}</strong>
                <p>{appointment.customer_notes}</p>
              </div>
            )}

            {appointment.partner_notes && (
              <div className="notes-section partner-notes">
                <strong>{t('appointments.providerNotes')}</strong>
                <p>{appointment.partner_notes}</p>
              </div>
            )}
          </Col>

          <Col xs={24} md={8} className="appointment-actions">
            <div className="timestamp-info">
              <small>{t('appointments.bookedOn', { date: dayjs(appointment.created_at).format('MMM D, YYYY') })}</small>
            </div>

            {appointment.status === 'PENDING' && (
              <Button
                danger
                block
                onClick={() => handleCancelAppointment(appointment)}
              >
                {t('appointments.cancelModal.title')}
              </Button>
            )}

            {appointment.status === 'CONFIRMED' && (
              <Alert
                message={t('appointments.confirmedAlert.title')}
                description={t('appointments.confirmedAlert.description')}
                type="success"
                showIcon
              />
            )}

            {appointment.status === 'COMPLETED' && (
              <>
                {!appointment.is_reviewed && !reviewedAppointments.has(appointment.id) ? (
                  <Button
                    type="primary"
                    block
                    icon={<StarOutlined />}
                    onClick={() => openReviewModal(appointment)}
                    style={{ marginBottom: 8 }}
                  >
                    {t('appointments.rateService')}
                  </Button>
                ) : (
                  <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginBottom: 8 }}>
                    {t('appointments.reviewed')}
                  </Tag>
                )}
                <Button block onClick={() => navigate('/services')}>
                  {t('appointments.bookAgain')}
                </Button>
              </>
            )}

            {appointment.status === 'CANCELLED' && (
              <Alert
                message={t('appointments.cancelledAlert.title')}
                description={appointment.cancelled_at
                  ? t('appointments.cancelledAlert.description', { date: dayjs(appointment.cancelled_at).format('MMM D, YYYY') })
                  : t('appointments.cancelledAlert.title')}
                type="error"
                showIcon
              />
            )}
          </Col>
        </Row>
      </Card>
    );
  };

  if (appointmentStore.loading && appointmentStore.myAppointments.length === 0) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="my-appointments-page">
      <div className="page-header">
        <div>
          <h1>{t('appointments.title')}</h1>
          <p>{t('appointments.description')}</p>
        </div>
        <Button type="primary" size="large" onClick={handleBookNew}>
          {t('appointments.bookNew')}
        </Button>
      </div>

      {appointmentStore.error && (
        <Alert
          message={t('common.error')}
          description={appointmentStore.error}
          type="error"
          closable
          onClose={() => appointmentStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="large"
        items={[
          {
            key: 'upcoming',
            label: t('appointments.tabs.upcoming', { count: upcomingAppointments.length }),
            children: upcomingAppointments.length === 0 ? (
              <Empty
                description={t('appointments.noUpcoming')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={handleBookNew}>
                  {t('appointments.bookFirst')}
                </Button>
              </Empty>
            ) : (
              <div className="appointments-list">
                {upcomingAppointments.map(renderAppointmentCard)}
              </div>
            )
          },
          {
            key: 'past',
            label: t('appointments.tabs.past', { count: pastAppointments.length }),
            children: pastAppointments.length === 0 ? (
              <Empty
                description={t('appointments.noPast')}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div className="appointments-list">
                {pastAppointments.map(renderAppointmentCard)}
              </div>
            )
          }
        ]}
      />

      {/* Review Modal */}
      {selectedAppointmentForReview && (
        <ReviewForm
          visible={reviewModalVisible}
          onClose={() => {
            setReviewModalVisible(false);
            setSelectedAppointmentForReview(null);
          }}
          onSuccess={handleReviewSuccess}
          type="service"
          itemId={selectedAppointmentForReview.service}
          referenceId={selectedAppointmentForReview.id}
          itemName={i18n.language === 'el'
            ? (selectedAppointmentForReview.service_name_el || selectedAppointmentForReview.service_name_en)
            : selectedAppointmentForReview.service_name_en}
        />
      )}
    </div>
  );
});

export default MyAppointmentsPage;
